import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { EmptyArt } from '../components/EmptyArt'

/*
  The File Cabinet.

  Every file the business ever put on the table, in one place, with a sentence saying what
  is inside it. The sentence is the whole feature: a list of forty statements named
  "Screenshot 2026-08-29 at 11.46.37 AM.png" is unusable, and opening each one to find the
  right year is the friction that stops an owner ever looking at his own paperwork.

  Opening is still there, one click, for when the sentence is not enough.
*/

interface CabinetDoc {
  id: string
  kind: string
  kindLabel: string
  fileName: string | null
  contentType: string | null
  sizeBytes: number
  periodLabel: string | null
  uploadedAt: string
  extractionStatus: 'none' | 'pending' | 'done' | 'scanned' | 'truncated' | 'failed'
  pageCount: number | null
  synopsis: string | null
  isImage: boolean
  readable: boolean
  note: string | null
  hasFile: boolean
  analyzed: boolean
  /** Which drawer it belongs in. Decided server-side so section and icon agree. */
  type: DocType
  typeLabel: string
  /** Which door it came in through. */
  source: string
  sourceLabel: string
}

type DocType = 'document' | 'spreadsheet' | 'image' | 'email'

/*
  The drawers, in the order they are worth opening.

  Documents first because that is most of the cabinet and what an owner is usually after.
  Email last because it is the newest door and, for now, the emptiest; it moves up on its
  own once people start forwarding, because a section with nothing in it is not drawn.
*/
const DRAWERS: { type: DocType; icon: () => React.ReactElement }[] = [
  { type: 'document',    icon: IconDoc },
  { type: 'spreadsheet', icon: IconSheet },
  { type: 'image',       icon: IconImage },
  { type: 'email',       icon: IconMail },
]

type Filter = 'all' | 'statements' | 'images' | 'unread'

export function Cabinet() {
  const [docs, setDocs] = useState<CabinetDoc[] | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(
    async () => setDocs(await api.get<CabinetDoc[]>('/api/cabinet')), [])

  useEffect(() => { void load().catch(() => setDocs([])) }, [load])

  /*
    Analysis runs in the worker, so a freshly uploaded file arrives with no synopsis and
    fills in a few seconds later. Polling only while something is actually outstanding.
  */
  useEffect(() => {
    if (!docs?.some(d => !d.analyzed || d.extractionStatus === 'pending')) return
    const id = setTimeout(() => { void load().catch(() => {}) }, 3000)
    return () => clearTimeout(id)
  }, [docs, load])

  if (!docs) return <div className="skeleton" style={{ width: '60%' }} />

  const shown = docs.filter(d => {
    if (filter === 'statements') return d.kind === 'profit_loss' || d.kind === 'balance_sheet'
    if (filter === 'images') return d.isImage
    if (filter === 'unread') return !d.readable
    return true
  })

  const unread = docs.filter(d => !d.readable).length

  return (
    <section className="cabinet">
      <header className="screen-head">
        <h1 className="screen-title">File Cabinet</h1>
        {/*
          No subtitle on an empty cabinet. The empty block below carries the same sentence
          as its title, and printing it twice on an otherwise blank screen was half the
          reason this page read as broken rather than new.
        */}
        {docs.length > 0 && (
          <p className="screen-sub">
            {docs.length} {docs.length === 1 ? 'file' : 'files'}, everything ever put on the table.
          </p>
        )}
      </header>

      {docs.length > 0 && (
        <div className="filters" role="tablist">
          <FilterTab id="all" now={filter} set={setFilter} label="Everything" count={docs.length} />
          <FilterTab
            id="statements" now={filter} set={setFilter} label="Statements"
            count={docs.filter(d => d.kind === 'profit_loss' || d.kind === 'balance_sheet').length}
          />
          <FilterTab
            id="images" now={filter} set={setFilter} label="Photos"
            count={docs.filter(d => d.isImage).length}
          />
          {unread > 0 && (
            <FilterTab id="unread" now={filter} set={setFilter} label="Unread" count={unread} />
          )}
        </div>
      )}

      {/*
        The house empty state, which is `empty` on its own. This screen had `card empty`,
        and the card is what put a bordered white box across the full width of an otherwise
        blank page with nothing in it but two lines of grey text. Every other empty state in
        the product is left-aligned on a measure with no border at all.

        It also told the owner where files come from without giving him anywhere to go. An
        empty screen is the one place a link is worth more than an explanation.
      */}
      {docs.length === 0 && (
        <div className="empty">
          <EmptyArt name="archive" />
          <div className="empty__title">Nothing filed yet</div>
          <p>
            Statements, photos, quotes, spreadsheets. Anything you hand the board is kept
            here for good, with a sentence saying what is in it, and any figures it holds
            carried into <a className="inline-link" href="#numbers">The Numbers</a>.
          </p>
          <p>
            Put the first one on <a className="inline-link" href="#table">The Table</a>, or
            attach it to your business under{' '}
            <a className="inline-link" href="#company">Company</a>.
          </p>
        </div>
      )}

      {DRAWERS.map(({ type, icon: Icon }) => {
        const items = shown.filter(d => d.type === type)
        if (items.length === 0) return null

        return (
          <section className="drawer" key={type}>
            {/*
              The heading carries the icon, and the rows underneath do not repeat it. A
              column of forty identical glyphs down the left of a list is decoration; one
              at the top of each section is a label.
            */}
            <h2 className="drawer__head">
              <span className={`drawer__icon drawer__icon--${type}`} aria-hidden><Icon /></span>
              {items[0]!.typeLabel}
              <span className="drawer__count">{items.length}</span>
            </h2>

            <ul className="filelist">
              {items.map(d => (
          <li key={d.id} className={`file${open === d.id ? ' file--open' : ''}`}>
            <button
              className="file__main"
              onClick={() => setOpen(open === d.id ? null : d.id)}
              aria-expanded={open === d.id}
            >
              <span className="file__body">
                <span className="file__name">{d.fileName ?? 'Untitled'}</span>

                <span className="file__meta">
                  {/*
                    Where it came in from, first. Every upload lands here whichever door it
                    came through, so the door is often the fastest way to recognise a file
                    called "Scan_20260904.pdf".
                  */}
                  <span className={`srcbadge srcbadge--${d.source}`}>{d.sourceLabel}</span>
                  {d.kindLabel}
                  {d.periodLabel ? ` · ${d.periodLabel}` : ''}
                  {' · '}{when(d.uploadedAt)}
                  {d.sizeBytes > 0 ? ` · ${size(d.sizeBytes)}` : ''}
                </span>

                <span className={`file__synopsis${d.synopsis ? '' : ' file__synopsis--waiting'}`}>
                  {d.synopsis ?? (d.analyzed ? 'No description.' : 'Reading it now…')}
                </span>
              </span>

              <span className="file__chev" aria-hidden><IconChevron /></span>
            </button>

            {open === d.id && (
              <div className="file__detail">
                {d.note && (
                  <p className="file__note">
                    <span className="file__notelabel">Your note</span>
                    {d.note}
                  </p>
                )}

                <p className="file__state">{readState(d)}</p>

                <div className="file__actions">
                  {d.hasFile && (
                    <a
                      className="btn btn--ghost"
                      href={`/api/documents/${d.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open the file
                    </a>
                  )}
                  {/* Removal lives here now rather than on the Company screen, which used
                      to list every file in the business under a heading that said "Profit
                      and loss". This is the screen whose whole job is the files. */}
                  <Remove id={d.id} onGone={load} />
                </div>

                {d.isImage && d.hasFile && (
                  <img className="file__preview" src={`/api/documents/${d.id}`} alt="" loading="lazy" />
                )}
              </div>
            )}
              </li>
            ))}
            </ul>
          </section>
        )
      })}
    </section>
  )
}

/*
  Removing a file, in two clicks.

  Two because it cannot be undone and it takes more with it than the file: every figure
  read out of a document is deleted with it, on purpose, so a statement removed because it
  was the wrong year does not leave its numbers behind arguing with the right one. That is
  the correct behaviour and it is also a surprise, so the confirm says it out loud.
*/
function Remove({ id, onGone }: { id: string; onGone: () => Promise<void> }) {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!asking) {
    return (
      <button className="btn btn--ghost btn--danger" onClick={() => setAsking(true)}>Remove</button>
    )
  }

  return (
    <span className="file__confirm">
      <span className="dim">Remove it? Its figures leave The Numbers too.</span>
      <button
        className="btn btn--danger"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          try {
            await api.del(`/api/company/documents/${id}`)
            await onGone()
          } finally { setBusy(false) }
        }}
      >
        {busy ? 'Removing' : 'Remove it'}
      </button>
      <button className="btn btn--ghost" onClick={() => setAsking(false)}>Keep it</button>
    </span>
  )
}

function FilterTab({
  id, now, set, label, count,
}: { id: Filter; now: Filter; set: (f: Filter) => void; label: string; count: number }) {
  return (
    <button
      role="tab"
      aria-selected={now === id}
      className={`filter${now === id ? ' filter--on' : ''}`}
      onClick={() => set(id)}
    >
      {label} <span className="filter__count">{count}</span>
    </button>
  )
}

/*
  A file that was read and a file that could not be read look identical in a list, and the
  difference decides whether the board is reasoning from these numbers or around them.
*/
function readState(d: CabinetDoc): string {
  switch (d.extractionStatus) {
    case 'pending':   return 'Being read now.'
    case 'done':      return d.pageCount
      ? `Read, ${d.pageCount} ${d.pageCount === 1 ? 'page' : 'pages'}. The board has this.`
      : 'Read. The board has this.'
    case 'truncated': return 'Long file. The board has the first part of it.'
    case 'scanned':   return d.note
      ? 'No text could be read, but the board has your note.'
      : 'No text could be read. Add a note and the board will have that.'
    case 'failed':    return 'This file could not be opened.'
    default:          return d.readable
      ? 'The board reads your note on this.'
      : 'The board cannot read this one.'
  }
}

function when(iso: string): string {
  const d = new Date(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function IconDoc() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </svg>
  )
}

function IconImage() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-5-5L5 20" />
    </svg>
  )
}

function IconSheet() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 4v16M15 4v16" />
    </svg>
  )
}

function IconMail() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </svg>
  )
}

function IconChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}
